import { useEffect, useState, useRef } from 'react';
import ErrorModal from "../modal/ErrorModal ";
import DomainNotRegisteredPage from "../../pages/DomainNotRegisteredPage";
import AltruonPaymentMethodSelector from './AltruonPaymentMethodSelector';
import { useStyles } from "../../context/StyleContext";
import { extractStyleCustomization, normalizeSimplifiedStyles } from "../../utils/styleUtils";
import { useCheckoutPage } from "../../hooks/useCheckoutPage";
import api from "../../api/api";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import {
    PageContainer,
    CheckoutContainer,
    LeftSection,
} from "./shared/CheckoutStyledComponents";


const AltruonCheckoutPage = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const sessionId = queryParams.get("session_id");
    const tenantId = window.location.hostname.split(".")[0];
    const { updateCustomStyles, styles } = useStyles();
    const { applySessionLocale, beginCheckoutSession } = useLanguage();
    const [checkoutProcessDto, setCheckoutProcessDto] = useState();
    const [paymentMethodPinnedAtCreate, setPaymentMethodPinnedAtCreate] = useState(false);
    const sessionFetchedRef = useRef(false);
    const sessionLanguageInitRef = useRef(null);


    // Use shared checkout hook
    const {
        checkoutPageConfDto,
        checkoutPageFields,
        routingEntries,
        platformItemDetails,
        isErrorModalVisible,
        itemDetails,
        domainStatus,
        currentDomain,
        isLoading,
        formData,
        isRoutingLoaded,
        billingPlatformId,
        planId,
        setErrorModalVisible,
        handleFormChange,
        updateBillingData,
    } = useCheckoutPage({ sessionId, catalogRequiresSessionId: true });

    // Fetch session data and update billing data
    useEffect(() => {
        const fetchSessionData = async () => {
            try {
                const response = await api.get(`/api/session/v1/${sessionId}`);

                if (response) {
                    const sessionKey = response?.data?.session_id || sessionId;
                    if (sessionLanguageInitRef.current !== sessionKey) {
                        sessionLanguageInitRef.current = sessionKey;
                        beginCheckoutSession(sessionKey);
                        const locale =
                            response?.data?.locale ||
                            response?.data?.checkoutProcessDto?.locale;
                        if (locale) {
                            applySessionLocale(locale);
                        }
                    }

                    const billingPlatformId = response?.data?.checkoutProcessDto?.billingData?.billingPlatformId;
                    const planId = response?.data?.checkoutProcessDto?.billingData?.planId;
                    const frequency = response?.data?.checkoutProcessDto?.billingData?.frequency;
                    const price = response?.data?.checkoutProcessDto?.paymentData?.amount;
                    const currency = response?.data?.checkoutProcessDto?.paymentData?.currency;

                    // Update billing data in the hook
                    updateBillingData(billingPlatformId, planId, frequency, price, currency);
                    setCheckoutProcessDto(response?.data?.checkoutProcessDto);
                    setPaymentMethodPinnedAtCreate(
                        response?.data?.payment_method_pinned_at_create === true
                    );

                    // Extract and apply style customization from session
                    const styleCustomization = extractStyleCustomization(response);
                    if (styleCustomization) {
                        const normalizedStyles = normalizeSimplifiedStyles(styleCustomization);
                        updateCustomStyles(normalizedStyles);
                    }
                }
            } catch (error) {
                console.error(error);
                toast.error(error?.response?.data?.message || "Something went wrong. Please try again later.");
            }
        };

        if (sessionId && !sessionFetchedRef.current) {
            sessionFetchedRef.current = true;
            fetchSessionData();
        }
    }, [sessionId, updateBillingData, updateCustomStyles, applySessionLocale, beginCheckoutSession]);

    // Render
    if (domainStatus === "checking" || isLoading) return null;

    if (domainStatus !== "valid") {
        return <DomainNotRegisteredPage domain={currentDomain} />;
    }

    return (
        <PageContainer $styles={styles} $transparent={!!sessionId}>
            <CheckoutContainer $styles={styles}>
                <LeftSection $styles={styles} $transparent={!!sessionId}>
                    <AltruonPaymentMethodSelector
                        routingEntries={routingEntries}
                        onChange={(method) => handleFormChange({ paymentMethod: method })}
                        itemDetails={itemDetails}
                        billingPlatformId={billingPlatformId}
                        billingPlatformName={itemDetails?.billingPlatformName}
                        planId={planId}
                        platformItemDetails={platformItemDetails}
                        checkoutPageConfDto={checkoutPageConfDto}
                        checkoutProcessDto={checkoutProcessDto}
                        paymentMethodPinnedAtCreate={paymentMethodPinnedAtCreate}
                        isLoading={isLoading}
                        isRoutingLoaded={isRoutingLoaded}
                        transparent={!!sessionId}
                        tenantId={tenantId}
                    />
                </LeftSection>
            </CheckoutContainer>
            <ErrorModal
                show={isErrorModalVisible}
                onClose={() => setErrorModalVisible(false)}
                message="This plan may no longer be available in its current form. Please contact the merchant for assistance."
                redirectUrl="/"
                buttonText="Go Back"
                variant="danger"
                icon="bi-exclamation-triangle-fill"
                size="lg"
                centered={true}
                animation="fade"
                showCountdown={false}
            />
        </PageContainer>
    );
};
export default AltruonCheckoutPage